import { useBilling } from '@hook'
import { InvoiceItemRes } from '@type/invoice.type'
import { Button, Modal } from '@ui'
import { getError, showToast } from '@util'
import { useEffect, useMemo, useState } from 'react'

export type DiscountType = 'percentage' | 'fixed_amount'

export type CategoryLine = {
  amount: number
  label: string
}

type DiscountableItem = Pick<InvoiceItemRes, 'id'> & {
  attributes: Pick<
    InvoiceItemRes['attributes'],
    'category' | 'name' | 'unit_price' | 'quantity' | 'discount_type' | 'discount_value' | 'discount_reason' | 'status'
  >
}

type ApplyDiscountProps = {
  categoryLines: CategoryLine[]
  invoiceId: string
  items: DiscountableItem[]
  onClose: () => void
}

function calculateDiscountAmount(subtotal: number, type: DiscountType, rawValue: string): number {
  const value = Number.parseFloat(rawValue)
  if (!Number.isFinite(value) || value <= 0 || subtotal <= 0) return 0
  if (type === 'percentage') return Math.min(subtotal, (subtotal * value) / 100)
  return Math.min(subtotal, value)
}

export function lineGross(item: DiscountableItem): number {
  return (Number.parseFloat(String(item.attributes.unit_price)) || 0) * (item.attributes.quantity || 0)
}

export function lineDiscount(item: DiscountableItem): number {
  const { discount_type: type, discount_value: value } = item.attributes
  if (!type || !value) return 0
  if (type !== 'percentage' && type !== 'fixed_amount') return 0
  return calculateDiscountAmount(lineGross(item), type, String(value))
}

export function lineNet(item: DiscountableItem): number {
  return Math.max(0, lineGross(item) - lineDiscount(item))
}

export function sumLineDiscounts(items: DiscountableItem[] | undefined): number {
  return (items ?? []).reduce((sum, item) => sum + lineDiscount(item), 0)
}

export function sumLineNets(items: DiscountableItem[] | undefined): number {
  return (items ?? []).reduce((sum, item) => sum + lineNet(item), 0)
}

export function sumLineGrosses(items: DiscountableItem[] | undefined): number {
  return (items ?? []).reduce((sum, item) => sum + lineGross(item), 0)
}

/** Legacy invoice-level discount (pre item-level migration). */
export function calculateInvoiceDiscount(
  subtotal: number,
  discountType?: string | null,
  discountValue?: string | null
): number {
  if (!discountType || !discountValue) return 0
  if (discountType !== 'percentage' && discountType !== 'fixed_amount') return 0
  return calculateDiscountAmount(subtotal, discountType, discountValue)
}

export function itemHasDiscount(item: DiscountableItem): boolean {
  return Boolean(item.attributes.discount_type && item.attributes.discount_value)
}

export function splitFixedDiscountAcrossItems(
  items: DiscountableItem[],
  totalDiscount: number
): { id: string; share: number }[] {
  const withGross = items.map(item => ({ id: item.id, gross: lineGross(item) }))
  const totalGross = withGross.reduce((sum, row) => sum + row.gross, 0)
  if (totalGross <= 0 || totalDiscount <= 0) return withGross.map(row => ({ id: row.id, share: 0 }))

  let remaining = Math.round(totalDiscount * 100) / 100
  return withGross.map((row, index) => {
    const share =
      index === withGross.length - 1 ? remaining : Math.round(((totalDiscount * row.gross) / totalGross) * 100) / 100
    const capped = Math.min(Math.max(share, 0), row.gross)
    remaining = Math.round((remaining - capped) * 100) / 100
    return { id: row.id, share: capped }
  })
}

function isDiscountEligible(item: DiscountableItem): boolean {
  const status = item.attributes.status
  return !status || status === 'pending'
}

export const ApplyDiscount = ({ categoryLines: _categoryLines, invoiceId, items, onClose }: ApplyDiscountProps) => {
  const { updateInvoice, updateItem } = useBilling({ id: invoiceId })
  const eligibleItems = useMemo(() => items.filter(isDiscountEligible), [items])
  const defaultSelectedIds = useMemo(
    () => eligibleItems.filter(item => !itemHasDiscount(item)).map(item => item.id),
    [eligibleItems]
  )

  const [selectedIds, setSelectedIds] = useState<string[]>(defaultSelectedIds)
  const [type, setType] = useState<DiscountType>('percentage')
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    setSelectedIds(defaultSelectedIds)
  }, [defaultSelectedIds])

  const selectedItems = useMemo(
    () => eligibleItems.filter(item => selectedIds.includes(item.id)),
    [eligibleItems, selectedIds]
  )
  const selectedGross = useMemo(() => sumLineGrosses(selectedItems), [selectedItems])
  const previewDiscount = useMemo(
    () => calculateDiscountAmount(selectedGross, type, amount),
    [amount, selectedGross, type]
  )
  const previewTotal = Math.max(0, selectedGross - previewDiscount)

  const toggleItem = (id: string) => {
    setSelectedIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]))
  }

  const toggleAll = () => {
    if (selectedIds.length === eligibleItems.length) {
      setSelectedIds([])
      return
    }
    setSelectedIds(eligibleItems.map(item => item.id))
  }

  const clearInvoiceLevelDiscount = async () => {
    await updateInvoice({
      formVal: {
        data: {
          id: invoiceId,
          type: 'invoice',
          attributes: {
            discount_type: null,
            discount_value: null,
            discount_reason: null
          }
        }
      },
      id: invoiceId
    })
  }

  const handleSubmit = async () => {
    const parsed = Number.parseFloat(amount)
    if (!Number.isFinite(parsed) || parsed <= 0) {
      showToast('Enter a valid discount amount')
      return
    }
    if (type === 'percentage' && parsed > 100) {
      showToast('Percentage cannot exceed 100')
      return
    }
    if (selectedItems.length === 0) {
      showToast('Select at least one item')
      return
    }

    const reasonValue = reason.trim() || null
    setIsSubmitting(true)
    try {
      if (type === 'percentage') {
        await Promise.all(
          selectedItems.map(item =>
            updateItem({
              formVal: {
                data: {
                  id: item.id,
                  type: 'invoice_item',
                  attributes: {
                    discount_type: 'percentage',
                    discount_value: amount.trim(),
                    discount_reason: reasonValue
                  }
                }
              },
              id: item.id
            })
          )
        )
      } else {
        const shares = splitFixedDiscountAcrossItems(selectedItems, parsed)
        await Promise.all(
          shares.map(({ id, share }) =>
            updateItem({
              formVal: {
                data: {
                  id,
                  type: 'invoice_item',
                  attributes: {
                    discount_type: 'fixed_amount',
                    discount_value: String(share),
                    discount_reason: reasonValue
                  }
                }
              },
              id
            })
          )
        )
      }

      await clearInvoiceLevelDiscount()
      showToast('Discount applied successfully')
      onClose()
    } catch (error) {
      getError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal onClose={onClose} style={{ minWidth: 'min(560px, calc(100vw - 1.5rem))' }} title="Apply discount">
      <div className="flex flex-col gap-5 px-6 pb-6 pt-2">
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm font-medium text-[#444]">Apply to items</span>
            <button className="text-sm font-medium text-[#1565C0] hover:underline" onClick={toggleAll} type="button">
              {selectedIds.length === eligibleItems.length ? 'Deselect all' : 'Select all'}
            </button>
          </div>
          <div className="max-h-48 overflow-y-auto rounded-lg border border-[#D9D9D9]">
            {eligibleItems.length === 0 ? (
              <p className="px-3 py-4 text-sm text-[#666]">No eligible items</p>
            ) : (
              eligibleItems.map(item => {
                const gross = lineGross(item)
                const checked = selectedIds.includes(item.id)
                return (
                  <label
                    className="flex cursor-pointer items-start gap-3 border-b border-[#EEEEEE] px-3 py-2.5 last:border-b-0"
                    key={item.id}
                  >
                    <input checked={checked} className="mt-1" onChange={() => toggleItem(item.id)} type="checkbox" />
                    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <span className="truncate text-sm text-[#111]">{item.attributes.name}</span>
                      <span className="text-xs capitalize text-[#777]">
                        {item.attributes.category?.replace(/_/g, ' ')}
                        {itemHasDiscount(item) ? ' · already discounted' : ''}
                      </span>
                    </span>
                    <span className="shrink-0 text-sm text-[#111]">NGN {gross.toLocaleString()}</span>
                  </label>
                )
              })
            )}
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-[#444]">Discount type</span>
          <div className="inline-flex w-fit overflow-hidden rounded-lg border border-[#D9D9D9]">
            <button
              className={`px-4 py-2 text-sm font-medium transition ${
                type === 'percentage' ? 'bg-[#F5EED6] text-[#111]' : 'bg-white text-[#666]'
              }`}
              onClick={() => setType('percentage')}
              type="button"
            >
              Percentage
            </button>
            <button
              className={`border-l border-[#D9D9D9] px-4 py-2 text-sm font-medium transition ${
                type === 'fixed_amount' ? 'bg-[#F5EED6] text-[#111]' : 'bg-white text-[#666]'
              }`}
              onClick={() => setType('fixed_amount')}
              type="button"
            >
              Fixed amount
            </button>
          </div>
        </div>

        <label className="flex flex-col gap-2">
          <span className="text-sm font-medium text-[#444]">Amount</span>
          <div className="relative">
            <input
              className="w-full rounded-lg border border-[#D9D9D9] px-3 py-2.5 pr-14 text-sm"
              inputMode="decimal"
              min="0"
              onChange={e => setAmount(e.target.value.replace(/[^\d.]/g, ''))}
              placeholder="0"
              type="text"
              value={amount}
            />
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#666]">
              {type === 'percentage' ? '%' : 'NGN'}
            </span>
          </div>
        </label>

        <label className="flex flex-col gap-2">
          <span className="text-sm font-medium text-[#444]">Reason (optional)</span>
          <textarea
            className="min-h-[88px] w-full resize-y rounded-lg border border-[#D9D9D9] px-3 py-2.5 text-sm"
            onChange={e => setReason(e.target.value)}
            placeholder="e.g. Staff courtesy, promo code, HMO adjustment"
            value={reason}
          />
        </label>

        <div className="rounded-xl bg-[#F5F5F5] px-4 py-4">
          <p className="mb-3 text-sm font-medium text-[#444]">Preview (selected items)</p>
          <div className="flex flex-col gap-2 text-sm">
            <div className="flex items-center justify-between gap-4">
              <span className="text-[#555]">Selected subtotal</span>
              <span className="text-[#111]">NGN {selectedGross.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-[#555]">Discount</span>
              <span className="text-[#2E7D32]">- NGN {previewDiscount.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between gap-4 border-t border-[#E0E0E0] pt-2">
              <span className="font-semibold text-[#111]">Selected net</span>
              <span className="font-semibold text-[#111]">NGN {previewTotal.toLocaleString()}</span>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-1">
          <Button kinds="outline" label="Cancel" onClick={onClose} size="sm" type="button" />
          <Button
            disabled={isSubmitting}
            label="Apply discount"
            onClick={() => void handleSubmit()}
            size="sm"
            type="button"
          />
        </div>
      </div>
    </Modal>
  )
}
